import type { ErrorCode } from '@boud/contracts';

/**
 * The single way the web client talks to the API.
 *
 * Three things it guarantees, so no caller has to remember them:
 *
 *  1. **The envelope is unwrapped.** Callers receive the resource, not
 *     `{data, meta}` (FRS §8.6.2).
 *  2. **Failures throw `ApiClientError`**, carrying the stable `code` and the
 *     server's already-localised `message`. UI branches on `code`; it displays
 *     `message`. Branching on message text breaks the moment the user switches
 *     language.
 *  3. **Credentials are always included**, because the refresh token is an
 *     httpOnly cookie the JavaScript cannot read — which is precisely why it is
 *     not in localStorage.
 */

export interface ApiErrorItem {
  code: ErrorCode;
  message: string;
  field: string | null;
}

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly errors: ApiErrorItem[],
    readonly requestId: string,
    readonly meta: Record<string, unknown> = {},
  ) {
    super(errors[0]?.message ?? 'Request failed');
    this.name = 'ApiClientError';
  }

  /** The leading error code — what a caller should branch on. */
  get code(): ErrorCode | undefined {
    return this.errors[0]?.code;
  }

  /** Field path → message, for rendering a form's errors all at once (§8.6.3). */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const error of this.errors) {
      if (error.field) result[error.field] = error.message;
    }
    return result;
  }
}

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * The in-memory access token.
 *
 * Never in localStorage or sessionStorage: anything an injected script can read,
 * it can exfiltrate, and a stolen access token is a valid session. Keeping it in
 * a module-scoped variable means it dies with the tab, and the httpOnly refresh
 * cookie is what survives a reload. Phase 1 wires the refresh flow.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Return the whole `{data, meta}` envelope rather than just `data`. */
  includeMeta?: boolean;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Silent refresh.
 *
 * The access token lives 15 minutes, which a user will routinely outlast while
 * filling in a form. Rather than show them a login screen and lose their work,
 * a 401 triggers one refresh attempt and the original request is replayed.
 *
 * The in-flight promise is shared, so a screen that fires six requests at once
 * performs **one** refresh rather than six. That matters beyond efficiency: six
 * concurrent rotations of the same token would look exactly like token reuse to
 * the server, and it would revoke the whole family — logging the user out for
 * doing nothing wrong.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh', undefined), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return false;

      const payload = (await response.json()) as { data?: { access_token?: string } };
      if (!payload.data?.access_token) return false;

      setAccessToken(payload.data.access_token);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all observe this result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/** Notified when the session ends for good, so the app can send the user to /login. */
type SessionEndedListener = () => void;
let onSessionEnded: SessionEndedListener | null = null;

export function setSessionEndedHandler(handler: SessionEndedListener | null): void {
  onSessionEnded = handler;
}

export interface PaginationMeta {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

/**
 * A collection request, returning `meta` alongside `data`.
 *
 * Collection endpoints carry their pagination facts in `meta` (FRS §8.6.6), and
 * `apiRequest` deliberately unwraps to `data` only. Rather than let callers
 * reach for `fetch` — which would lose the Authorization header and the silent
 * refresh — they use this.
 */
export async function apiRequestPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ items: T[]; meta: PaginationMeta }> {
  const envelope = await apiRequest<T[]>(path, { ...options, includeMeta: true });
  const { data, meta } = envelope as unknown as { data: T[]; meta: PaginationMeta };
  return { items: data, meta };
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await performRequest<T>(path, options);
  } catch (error) {
    const isExpiredSession =
      error instanceof ApiClientError &&
      error.status === 401 &&
      (error.code === 'token_expired' || error.code === 'token_invalid');

    // Not retried on `authentication_required` — that means no token was sent
    // at all, so there is nothing to refresh.
    if (!isExpiredSession) throw error;

    if (await attemptRefresh()) return performRequest<T>(path, options);

    setAccessToken(null);
    onSessionEnded?.();
    throw error;
  }
}

async function performRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  const response = await fetch(buildUrl(path, query), {
    method,
    signal: signal ?? null,
    // Sends and receives the httpOnly refresh cookie.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      // The server localises its own messages; this tells it which language.
      'Accept-Language': document.documentElement.lang || 'en',
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const requestId = response.headers.get('x-request-id') ?? 'unknown';

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as {
    data?: T;
    errors?: ApiErrorItem[];
    meta?: Record<string, unknown>;
  } | null;

  if (!response.ok || payload?.errors) {
    throw new ApiClientError(
      response.status,
      payload?.errors ?? [
        { code: 'internal_error', message: 'Something went wrong.', field: null },
      ],
      requestId,
      payload?.meta ?? {},
    );
  }

  return (options.includeMeta ? payload : payload?.data) as T;
}
